import { DefaultImportEntityTemplate, ImportColumnDefinition, ImportEntityTemplate, isImportColumnDefinition } from "aderant-conflicts-models";
import { ReactSpreadsheetImport } from "@aderant/react-spreadsheet-import";
import PageContent from "components/PageContainer/PageContent";
import React, { useMemo, useRef } from "react";
import { useDispatch } from "react-redux";
import * as RSI from "@aderant/react-spreadsheet-import/types/types";
import { Columns } from "@aderant/react-spreadsheet-import/types/steps/MatchColumnsStep/MatchColumnsStep";
import { consolidateDataUK, consolidateDataOFAC, consolidateDataASO, consolidateDataEU } from "./concatenationUtils";
import { adminActions } from "state/actions/AdminActions";

export function mapImportColumnDefinition(columns: ImportColumnDefinition[]): RSI.Field<string>[] {
    const fields = columns.map((column) => {
        return {
            label: column.propertyLabel,
            key: column.propertyName,
            alternateMatches: column.alternateMatches || [],
            fieldType: column.fieldType || { type: "input" },
            validations: column.validations || [{ rule: "required", errorMessage: `${column.propertyLabel} is required.`, level: "hidden" }]
        };
    });
    return fields;
}

export type EntitySpreadsheetImportProps = {
    // The name of the entity to import data for.
    entityName: string;
    // Determines if modal is visible.
    isOpen: boolean;
    // Use to update isOpen.
    setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;

    showDataDialog?: boolean;

    setShowDataDialog?: React.Dispatch<React.SetStateAction<boolean>>;
};
//TODO - US59978: This is the first pass at the EntitySpreadsheetImport UI. It will need to be updated to match the final design.
export function EntitySpreadsheetImport(props: EntitySpreadsheetImportProps) {
    const { entityName, isOpen, setIsOpen, showDataDialog, setShowDataDialog } = props;
    const columnMapping = useRef<Columns<string>>([]);

    const dispatch = useDispatch();

    const handleSubmit = (data: RSI.Result<string>, file: File) => {
        const dataSize = data.validData.length;
        console.log("Data size: ", dataSize);

        if (dataSize >= 70000) {
            if (setShowDataDialog) {
                setShowDataDialog(true);
            }
            return;
        }

        let modifiedData = data;
        switch (entityName) {
            case "sanctionsuk":
                modifiedData = { ...data, validData: consolidateDataUK(data.validData, "id") };
                break;
            case "sanctionsofac":
                modifiedData = { ...data, validData: consolidateDataOFAC(data.validData, "id") };
                break;
            case "sanctionsaso":
                modifiedData = { ...data, validData: consolidateDataASO(data.validData, "id") };
                break;
            case "sanctionseu":
                modifiedData = { ...data, validData: consolidateDataEU(data.validData, "id") };
                break;
            default:
                break;
        }
        modifiedData.all = [];
        dispatch(
            adminActions.saveSanctionsList({
                entityName,
                data: modifiedData,
                file,
                columnMapping: columnMapping.current
            })
        );
    };

    function handleClose() {
        if (setIsOpen) {
            setIsOpen(false);
        }
    }

    function afterMatchColumns(table: RSI.Data<string>[], rawData: RSI.RawData[], columns: Columns<string>): Promise<RSI.Data<string>[]> {
        columnMapping.current = columns;
        return Promise.resolve(table);
    }

    const fields = useMemo(() => {
        if (!entityName && isOpen) {
            handleClose();
        }
        const templates: ImportEntityTemplate[] = DefaultImportEntityTemplate?.filter((t) => t?.entityName === entityName);
        if (templates.length === 0) {
            console.log("No templates found for entity: " + entityName);
            handleClose();
        }
        const template = templates[0];
        const importColumns = template?.columns?.filter((c) => isImportColumnDefinition(c));
        // We know that importColumns is an array of ImportColumnDefinition, so we can safely cast it.
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        return mapImportColumnDefinition(importColumns as ImportColumnDefinition[]);
    }, [entityName]);

    if (fields.length === 0) {
        console.log("No import fields found in template for entity: " + entityName);
        handleClose();
    }

    return (
        <PageContent>
            <ReactSpreadsheetImport
                isOpen={isOpen}
                onClose={handleClose}
                onSubmit={handleSubmit}
                fields={fields}
                isNavigationEnabled={true}
                persistStateOnNavigation={true}
                matchColumnsStepHook={afterMatchColumns}
                autoMapHeaders={true}
                allowInvalidSubmit={false}
                translations={{
                    alerts: {
                        unmatchedRequiredFields: {
                            headerTitle: "Incomplete Column Matching",
                            bodyText: "All columns must be matched in order to continue. Ensure all columns are matched before trying again.",
                            cancelButtonTitle: "Close"
                        }
                    }
                }}
                customTheme={{
                    components: {
                        UploadStep: {
                            baseStyle: {
                                tableWrapper: {
                                    mb: "0", 
                                    position: "relative",
                                    h: "auto",
                                    minH: "auto", 
                                    maxH: "none",
                                    overflow: "visible",
                                    "& .rdg": {
                                        height: "auto !important",
                                        minHeight: "auto !important",
                                        blockSize: "auto !important",
                                        flex: "none !important",
                                    }
                                },
                                dropzone: {
                                    flex: "1",
                                    minHeight: "400px",
                                    display: "flex",
                                    flexDirection: "column",
                                    justifyContent: "center",
                                    alignItems: "center",
                                    border: "2px dashed",
                                    borderColor: "rsi.500",
                                    borderRadius: "lg",
                                    bg: "background",
                                    mt: "0",
                                    p: "2rem",
                                }
                            }
                        }
                    },
                    styles: {
                        global: {
                            ".rdg": {
                                border: "none !important",
                                "--rdg-border-color": "transparent"
                            },
                            ".rdg-cell": {
                                borderRight: "none !important",
                                borderBottom: "none !important"
                            },
                            "[data-testid='rsi-upload-step'], [data-testid='upload-step'], .upload-step": {
                                display: "flex !important",
                                flexDirection: "column !important",
                                height: "100% !important",
                            }
                        }
                    }
                }}
                disableConfirmValidationIfError={true}
            />
        </PageContent>
    );
}
